'use client';

import { DataPipelineCount, DatasetPipelineList, DatasetPipelineStatus, Perturbation, TaskTypeEnum } from '@api';
import {
  DataTableColumnHeader,
  DataTableSearchColumnHeader,
  DataTableDateRangeColumnHeader,
  dateRangeFilterFn,
  textFilterFn,
} from '@components/table';
import { DataTableFacetedFilter } from '@components/table/DataTableFacetedFilter/DataTableFacetedFilter';
import { EmptyValue, RelatedLinkCell, RowLinkCell, StatusTag, Tooltip } from '@components/ui';
import { MapperHelper, formatPerturbationDisplayName } from '@helpers';
import { getI18n } from '@i18n';
import { taskTypeLabelMap, taskTypeOptions } from '@lib';
import { Progress } from '@registry/new-york/ui/progress';
import { datasetInventoryCounterStore } from '@stores';
import { ColumnDef } from '@tanstack/react-table';
import { AppRoutes } from '@types';

import styles from './Columns.module.scss';
import { DataTableRowActions } from './DataTableRowActions';
import { PredictionsCell } from './PredictionsCell';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const t = (key: any) => getI18n().t(key) as string;

export function getDatasetColumns(
  counts?: DataPipelineCount,
  excludeConcierge = false, // exclude it in the selection dataset step
): ColumnDef<DatasetPipelineList>[] {
  const statusList = counts
    ? [
        { value: DatasetPipelineStatus.pending, count: counts.statusCount.pending },
        { value: DatasetPipelineStatus.running, count: counts.statusCount.running },
        { value: DatasetPipelineStatus.completed, count: counts.statusCount.completed },
        { value: DatasetPipelineStatus.failed, count: counts.statusCount.failed },
        { value: DatasetPipelineStatus.cancelled, count: counts.statusCount.cancelled },
      ]
    : undefined;
  const taskTypeList = counts
    ? [
        { value: TaskTypeEnum.tabularMl, count: counts.taskTypeCount.tabularMl },
        { value: TaskTypeEnum.singleTurnChat, count: counts.taskTypeCount.singleTurnChat },
        { value: TaskTypeEnum.conversationalAgent, count: counts.taskTypeCount.conversationalAgent },
        { value: TaskTypeEnum.textGeneration, count: counts.taskTypeCount.textGeneration },
        {
          value: TaskTypeEnum.informationExtraction,
          count: counts.taskTypeCount.informationExtraction,
        },
        { value: TaskTypeEnum.voiceAgent, count: counts.taskTypeCount.voiceAgent },
        { value: TaskTypeEnum.concierge, count: counts.taskTypeCount.concierge },
      ].filter(item => !excludeConcierge || item.value !== TaskTypeEnum.concierge)
    : undefined;
  const taskTypeFilterOptions = excludeConcierge
    ? taskTypeOptions.filter(option => option.value !== TaskTypeEnum.concierge)
    : taskTypeOptions;
  const modelNameList = counts
    ? Object.entries(counts.modelNameCount).map(([value, count]) => ({ value, count }))
    : undefined;

  return [
    {
      accessorKey: 'taskName',
      size: 260,
      header: ({ column }) => (
        <DataTableSearchColumnHeader
          column={column}
          title={t('dataset:table.columns.name')}
          truncate
        />
      ),
      cell: ({ row }) => (
        <RowLinkCell href={`${AppRoutes.datasetInventory}/${row.original.id}/`}>
          {row.getValue('taskName') || <EmptyValue />}
        </RowLinkCell>
      ),
      enableHiding: false,
      filterFn: textFilterFn,
    },
    {
      accessorKey: 'status',
      header: ({ column, table }) => (
        <DataTableFacetedFilter
          column={column}
          title="Status"
          options={Object.values(DatasetPipelineStatus).map(s => ({
            label: <div>{s.toUpperCase()}</div>,
            value: s,
          }))}
          list={statusList}
          store={datasetInventoryCounterStore}
          setLoader={(val: boolean) => datasetInventoryCounterStore.setLoader(val)}
          hideSelectedBadges
          hasActiveFilters={table.getState().columnFilters?.length > 0}
          truncate
        />
      ),
      cell: ({ row }) => {
        const status = row.getValue('status') as DatasetPipelineStatus;
        if (status === 'running') {
          const { progress } = row.original;
          if (progress < 100) {
            return (
              <div className={styles.progressContainer}>
                <Progress
                  className={styles.progress}
                  value={progress}
                />
              </div>
            );
          }
        }
        const tag = <StatusTag status={status} />;
        if (status === DatasetPipelineStatus.failed && row.original.errorMessage) {
          return (
            <Tooltip
              content={row.original.errorMessage}
              placement="top"
              isFixed
              className={styles.errorTooltip}
            >
              {tag}
            </Tooltip>
          );
        }
        return tag;
      },
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      id: 'aiModelName',
      size: 220,
      accessorFn: row => row.aiModel?.name,
      header: ({ column, table }) => (
        <DataTableFacetedFilter
          column={column}
          title="Model"
          options={(modelNameList ?? []).map(({ value }) => ({ label: <div>{value}</div>, value }))}
          list={modelNameList}
          store={datasetInventoryCounterStore}
          setLoader={(val: boolean) => datasetInventoryCounterStore.setLoader(val)}
          hideSelectedBadges
          hasActiveFilters={table.getState().columnFilters?.length > 0}
          truncate
        />
      ),
      cell: ({ row }) =>
        row.original.aiModel ? (
          <RelatedLinkCell
            href={`${AppRoutes.modelDetails}/${row.original.aiModel.id}`}
            singleLine
          >
            {row.original.aiModel.name}
          </RelatedLinkCell>
        ) : (
          <EmptyValue />
        ),
      enableSorting: false,
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: 'taskType',
      size: 210,
      minSize: 200,
      accessorFn: row => row.taskType,
      header: ({ column, table }) => (
        <DataTableFacetedFilter
          column={column}
          title="Model task type"
          options={taskTypeFilterOptions}
          list={taskTypeList}
          store={datasetInventoryCounterStore}
          setLoader={(val: boolean) => datasetInventoryCounterStore.setLoader(val)}
          hideSelectedBadges
          hasActiveFilters={table.getState().columnFilters?.length > 0}
          truncate
        />
      ),
      cell: ({ row }) => {
        const aiModelTaskType = row.original.taskType;
        return <span className={styles.nowrap}>{taskTypeLabelMap[aiModelTaskType]}</span>;
      },
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: 'startTime',
      size: 210,
      minSize: 200,
      header: ({ column }) => (
        <DataTableDateRangeColumnHeader
          column={column}
          title="Started at"
        />
      ),
      cell: ({ row }) => (
        <span className={styles.dateCell}>
          {row.getValue('startTime') ? MapperHelper.formatDateWithHour(row.getValue('startTime')) : 'not started'}
        </span>
      ),
      enableGlobalFilter: false,
      filterFn: dateRangeFilterFn,
    },
    {
      accessorKey: 'endTime',
      size: 210,
      minSize: 200,
      header: ({ column }) => (
        <DataTableDateRangeColumnHeader
          column={column}
          title="Finished at"
        />
      ),
      cell: ({ row }) => (
        <span className={styles.dateCell}>
          {row.getValue('endTime') ? MapperHelper.formatDateWithHour(row.getValue('endTime')) : 'not finished'}
        </span>
      ),
      enableGlobalFilter: false,
      filterFn: dateRangeFilterFn,
    },
    {
      accessorKey: 'predictions',
      // TODO: empty state for not finished dataset generation and finilize logic
      // TODO: for synthetic datasets, dataset object is null then
      accessorFn: row => (row as any).predictions,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Predictions"
          truncate
        />
      ),
      cell: ({ row }) => {
        const originalDataset = row.original?.dataset;
        const perturbedDatasets = row.original?.perturbedDatasets;
        return (
          <PredictionsCell
            rowIndex={row.index}
            originalDatasetPrediction={originalDataset?.hasPredictions}
            perturbedDatasetsPredictions={perturbedDatasets?.reduce((acc, dataset) => {
              acc[formatPerturbationDisplayName(dataset.perturbation)] = dataset.hasPredictions;
              return acc;
            }, {})}
          />
        );
      },
      enableGlobalFilter: false,
    },
    {
      accessorKey: 'perturbations',
      header: ({ column }) => (
        <DataTableSearchColumnHeader
          column={column}
          title="Perturbation types"
          truncate
        />
      ),
      cell: ({ row }) => {
        const perturbations = row.getValue('perturbations') as Perturbation[];
        return (
          <>
            {perturbations?.length === 0 && <EmptyValue />}
            {perturbations?.length > 0 && (
              <div className={styles.scroll}>
                {perturbations.map(perturbation => (
                  <div
                    key={perturbation.id}
                    className={styles.row}
                  >
                    {formatPerturbationDisplayName(perturbation)}
                  </div>
                ))}
              </div>
            )}
          </>
        );
      },
      filterFn: (row, id, value) => {
        const filterStr = String((value as string[])?.[0] ?? '').toLowerCase();
        if (!filterStr) return true;
        const perturbations = row.getValue(id) as Perturbation[];
        return (
          perturbations?.some(
            p =>
              formatPerturbationDisplayName(p).toLowerCase().includes(filterStr) ||
              p.name.toLowerCase().includes(filterStr),
          ) ?? false
        );
      },
      enableGlobalFilter: false,
    },
    {
      id: 'actions',
      meta: { fixed: true },
      cell: ({ row, table }) => (
        <DataTableRowActions
          row={row}
          table={table}
        />
      ),
    },
  ];
}

export const columns: ColumnDef<DatasetPipelineList>[] = getDatasetColumns();
